// MyGiving — donor portal donations history page (β.1 / CHG-1495).
//
// Donor-scope analogue of admin Donations.tsx. Consumes GET
// /api/v1/me/donations (α.2 / CHG-1479) via the listMyDonations helper.
// Donor-tone copy: title "My giving", empty-state for new donors (not
// the admin's "appears here as soon as your first gift is recorded").
// No admin filters: no donor-search (caller IS the donor), no export,
// no bulk actions.
//
// Columns per the β.1 brief: recorded date, amount, designation,
// campaign, status. Same column-type primitives as the admin page so
// formatting (currency, relative-date) is consistent across surfaces.

import type { ReactNode } from "react";
import { ListPage, type ListPageConfig } from "../components/composition/ListPage";
import { useFormatters } from "../contexts/IntlContext";
import { meDonationsApi } from "../api/me-donations";
import type { PortalDonationRead } from "../api/me-overview";

const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  succeeded: "Captured",
  refunded: "Refunded",
  failed: "Failed",
};

function designationLabel(row: PortalDonationRead): string {
  return row.designation_name ?? "—";
}

function organizationLabel(row: PortalDonationRead): string {
  // CHG-1513 wave 1 — PortalDonationRead has no campaign_name, so the
  // Campaign column from the original β.1 build is replaced by an
  // Organization column (org_name). Donors care about org first when
  // browsing their own cross-org history; campaign attribution is admin-
  // internal. A wave 2/3 BE extension could surface campaign_name if
  // product wants it; until then this is the donor-meaningful slot.
  return row.org_name ?? "—";
}

// CHG-2934 (§8 decision 6 — EXPOSE) — refunded / partially-refunded gifts
// lead with the NET (effective) amount + a refunded caption so the list
// reconciles with the net summary lifetime figure. Component (not a bare
// render fn) so it can use the useFormatters hook.
function DonationAmountCell({ donation }: { donation: PortalDonationRead }) {
  const fmt = useFormatters();
  const refunded = donation.refunded_amount_minor_units ?? 0;
  if (refunded <= 0) {
    return (
      <span className="tabular">
        {fmt.formatCurrency(donation.amount_minor_units, donation.currency)}
      </span>
    );
  }
  const effective =
    donation.effective_amount_minor_units ??
    Math.max(0, donation.amount_minor_units - refunded);
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
      <span className="tabular">{fmt.formatCurrency(effective, donation.currency)}</span>
      <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
        {fmt.formatCurrency(refunded, donation.currency)} refunded
      </span>
    </span>
  );
}

function amountColumn(row: PortalDonationRead): ReactNode {
  return <DonationAmountCell donation={row} />;
}

function statusColumn(row: PortalDonationRead): ReactNode {
  const label = STATUS_LABEL[row.status] ?? row.status;
  // CHG-2934 — a partially refunded gift keeps status=succeeded; annotate
  // the partial refund so the reduced amount is never unexplained.
  if ((row.refunded_amount_minor_units ?? 0) > 0 && row.status !== "refunded") {
    return `${label} — partially refunded`;
  }
  return label;
}

const myGivingListConfig: ListPageConfig<PortalDonationRead> = {
  title: "My giving",
  subtitle:
    "Every gift you've made — across every organization you give to. Search, review, download receipts.",
  queryKey: ["me", "donations", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search }) =>
    meDonationsApi.listMyDonations({ page, pageSize, sortField, sortDirection, search }),
  defaultSort: { field: "created_at", direction: "desc" },
  pageSize: 25,
  // CHG-1517 wave 2 — rows navigate to MyDonationDetail at the
  // /my-giving/donations/:donationId route. Donor-scope detail uses the
  // donationId param (not :id) to match the DetailPage idParam convention.
  rowHref: "/my-giving/donations/{id}",
  emptyState: {
    title: "You haven't given yet",
    subtitle:
      "When you make a donation through this site or any organization on Alora Giving, it'll appear here.",
  },
  columns: [
    {
      field: "created_at",
      label: "Recorded",
      type: "relative-date",
      sortable: true,
    },
    {
      // CHG-2934 — custom render: NET amount + refunded caption for
      // refunded / partially-refunded gifts (gross otherwise).
      field: "amount_minor_units",
      label: "Amount",
      type: "custom",
      sortable: true,
      render: amountColumn,
    },
    {
      field: "designation_id",
      label: "Designation",
      type: "custom",
      render: designationLabel,
    },
    {
      field: "organization_id",
      label: "Organization",
      type: "custom",
      render: organizationLabel,
    },
    {
      field: "status",
      label: "Status",
      type: "custom",
      sortable: true,
      render: statusColumn,
    },
  ],
};

export function MyGiving() {
  return <ListPage config={myGivingListConfig} />;
}
